(() => {
  const origin = 'https://khaki-firsthand-nonlinearprogramming.replit.app';
  const protocol = 'paratito-inquiry/v1';
  const views = ['front', 'left', 'right', 'desk', 'meeting', 'terrace'];
  const uuid = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  const positive = value => typeof value === 'number' && Number.isFinite(value) && value > 0;
  const dimensions = size => size === 650 ? [74, 255] : size === 1200 ? [91, 273] : [91, 240];
  const getJson = async path => {
    const response = await fetch(origin + path, { credentials: 'omit', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('unavailable');
    return response.json();
  };
  const image = blob => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const result = new Image();
    result.onload = () => { URL.revokeObjectURL(url); resolve(result); };
    result.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image')); };
    result.src = url;
  });
  const canvasBlob = (canvas, type) => new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('image')), type, .9));
  const logoArtwork = (photo, removeBackground, orientation) => {
    const width = photo.naturalWidth, height = photo.naturalHeight;
    if (!width || !height || width > 16384 || height > 16384 || width * height > 40000000) throw new Error('logo');
    const source = document.createElement('canvas'); source.width = width; source.height = height;
    const context = source.getContext('2d', { willReadFrequently: true }); context.drawImage(photo, 0, 0);
    const pixels = context.getImageData(0, 0, width, height);
    const data = pixels.data;
    const remove = removeBackground && [3, (width - 1) * 4 + 3, (height - 1) * width * 4 + 3, (width * height - 1) * 4 + 3].every(offset => data[offset] >= 250);
    let background = [255, 255, 255], darkest = 255, ink = [0, 0, 0], count = 0;
    if (remove) {
      const channels = [[], [], []];
      const sample = (x, y) => {
        const offset = (y * width + x) * 4, rgb = [data[offset], data[offset + 1], data[offset + 2]];
        if (data[offset + 3] >= 250 && Math.min(...rgb) >= 225 && Math.max(...rgb) - Math.min(...rgb) <= 38) rgb.forEach((value, channel) => channels[channel].push(value));
      };
      const stride = Math.max(1, Math.ceil((2 * width + 2 * height) / 2048));
      for (let x = 0; x < width; x += stride) { sample(x, 0); sample(x, height - 1); }
      for (let y = 0; y < height; y += stride) { sample(0, y); sample(width - 1, y); }
      background = channels.map(values => { values.sort((a, b) => a - b); return values.length ? values[Math.floor(values.length / 2)] : 255; });
      for (let offset = 0; offset < data.length; offset += 4) if (data[offset + 3]) darkest = Math.min(darkest, .2126 * data[offset] + .7152 * data[offset + 1] + .0722 * data[offset + 2]);
      if (darkest < 200) {
        for (let offset = 0; offset < data.length; offset += 4) if (data[offset + 3] && .2126 * data[offset] + .7152 * data[offset + 1] + .0722 * data[offset + 2] <= darkest + 16) { for (let channel = 0; channel < 3; channel++) ink[channel] += data[offset + channel]; count++; }
        if (count) ink = ink.map(value => value / count);
      }
    }
    const vector = ink.map((value, channel) => background[channel] - value), squared = vector.reduce((total, value) => total + value * value, 0);
    const threshold = Math.min(...background) - 1 - 74 * Math.pow(.25, 1.5);
    let left = width, top = height, right = -1, bottom = -1;
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
      const offset = (y * width + x) * 4;
      let alpha = data[offset + 3];
      if (alpha && remove) {
        const rgb = [data[offset], data[offset + 1], data[offset + 2]];
        let edge = null;
        if (count && squared > 0) {
          const fraction = Math.max(0, Math.min(1, rgb.reduce((sum, value, channel) => sum + (background[channel] - value) * vector[channel], 0) / squared));
          if (Math.sqrt(rgb.reduce((sum, value, channel) => sum + (value - (background[channel] - fraction * vector[channel])) ** 2, 0)) <= 28) edge = fraction;
        }
        const minimum = Math.min(...rgb), colour = Math.max(...rgb) - minimum;
        const grey = Math.max(0, Math.min(1, (56 - colour) / 36)), white = Math.max(0, Math.min(1, (minimum - (threshold - 18)) / 18));
        alpha = Math.round(alpha * (edge ?? 1) * (1 - white * white * (3 - 2 * white) * grey));
      }
      data[offset] = 0; data[offset + 1] = 27; data[offset + 2] = 38; data[offset + 3] = alpha;
      if (alpha) { left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y); }
    }
    if (right < left || bottom < top) throw new Error('logo');
    context.putImageData(pixels, 0, 0);
    const croppedWidth = right - left + 1, croppedHeight = bottom - top + 1;
    const vertical = orientation === 'vertical';
    const canvas = document.createElement('canvas'); canvas.width = vertical ? croppedHeight : croppedWidth; canvas.height = vertical ? croppedWidth : croppedHeight;
    const drawing = canvas.getContext('2d');
    if (vertical) { drawing.translate(canvas.width, 0); drawing.rotate(Math.PI / 2); }
    drawing.drawImage(source, left, top, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight);
    return { canvas, width: croppedWidth, height: croppedHeight };
  };
  const validatePackage = value => {
    if (!value || value.schemaVersion !== 1 || value.isPlaceholder !== false || !Number.isFinite(Date.parse(value.createdAt))) return false;
    const settings = value.settings;
    const logo = value.originalFile;
    const measure = settings?.engravingDimensions;
    if (!settings || !['horizontal', 'vertical'].includes(settings.orientation) || !positive(settings.size) || settings.size < 25 || settings.size > 100 || typeof settings.removeBackground !== 'boolean' || !views.includes(settings.selectedView)) return false;
    if (!measure || !positive(measure.widthMm) || !positive(measure.heightMm) || measure.widthMm > 1000 || measure.heightMm > 2000 || measure.fitsPreview !== true || !['estimated', 'requested'].includes(measure.mode)) return false;
    if (!logo || typeof logo.name !== 'string' || logo.name.length > 240 || !(logo.bytes instanceof Blob) || logo.size !== logo.bytes.size || !logo.size || logo.size > 8 * 1024 * 1024 || !['image/png', 'image/jpeg', 'image/svg+xml'].includes(logo.type)) return false;
    if (!value.previews || !views.every(view => value.previews[view] instanceof Blob && value.previews[view].type === 'image/png' && value.previews[view].size > 0 && value.previews[view].size < 8 * 1024 * 1024)) return false;
    return views.reduce((total, view) => total + value.previews[view].size, logo.size) < 32 * 1024 * 1024;
  };
  const draftStore = (mode, operation) => new Promise((resolve,reject) => {
    const request=indexedDB.open('paratito-inquiry-designs',1);
    request.onupgradeneeded=()=>request.result.createObjectStore('designs',{keyPath:'id'});
    request.onerror=()=>reject(request.error);
    request.onsuccess=()=>{
      const db=request.result, transaction=db.transaction('designs',mode);
      let result;
      try { const action=operation(transaction.objectStore('designs')); action.onsuccess=()=>{result=action.result;}; }
      catch(error) { db.close(); reject(error); return; }
      transaction.oncomplete=()=>{db.close();resolve(result);};
      transaction.onerror=transaction.onabort=()=>{db.close();reject(transaction.error);};
    };
  });
  const deleteDraft = id => { if(uuid(id)) draftStore('readwrite',store=>store.delete(id)).catch(()=>{}); };
  const matchesNativeReturn = (receipt, search) => !!receipt && new URLSearchParams(search).get('pt_request') === receipt.requestId;
  const contactRequests = new Map();
  const completeContact = async (receipt, { storageKey, onMarketingStatus, retryMarketing = false }) => {
    if (!uuid(receipt?.requestId) || !/^PT-\d{8}-[0-9A-F]{8}$/.test(receipt.inquiryId || '')) return;
    const contact = receipt.contact;
    if (!contact || typeof contact.name !== 'string' || !contact.name.trim() || contact.name.length > 120 || typeof contact.company !== 'string' || !contact.company.trim() || contact.company.length > 160 || typeof contact.email !== 'string' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email) || contact.email.length > 254 || typeof contact.marketingOptIn !== 'boolean') return;
    let key;
    try {
      const url = new URL(receipt.pdfUrl);
      key = url.searchParams.get('key');
      if (url.origin !== origin || url.username || url.password || url.hash || url.pathname !== `/api/business/downloads/${receipt.requestId}/pdf` || !/^[a-f0-9]{64}$/.test(key || '') || [...url.searchParams].length !== 1) return;
    } catch { return; }
    if (contactRequests.has(receipt.requestId)) return contactRequests.get(receipt.requestId);
    const persistContact = () => {
      try {
        const current = JSON.parse(sessionStorage.getItem(storageKey));
        if (current?.receipt?.requestId === receipt.requestId) sessionStorage.setItem(storageKey, JSON.stringify({ ...current, receipt }));
      } catch {}
    };
    const run = async () => {
      if (receipt.commitVerified !== true || !['saved', 'pending'].includes(receipt.captureStatus)) {
        try {
          const response = await fetch(`${origin}/api/business/inquiries/${receipt.requestId}/submit`, {
            method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ key }), signal: AbortSignal.timeout(12000)
          });
          const result = await response.json();
          if (response.status !== 200 || result.status !== 'submitted' || result.requestId !== receipt.requestId || result.inquiryId !== receipt.inquiryId || !['saved', 'pending', 'failed'].includes(result.klaviyo?.capture)) throw new Error('capture');
          receipt.captureStatus = result.klaviyo.capture;
          receipt.commitVerified = true;
        } catch { receipt.captureStatus = 'failed'; receipt.commitVerified = false; }
        persistContact();
      }
      if (!contact.marketingOptIn) return;
      if (receipt.marketingStatus === 'accepted') { onMarketingStatus('accepted'); return; }
      if (receipt.commitVerified !== true) { onMarketingStatus('failed'); return; }
      if (!retryMarketing && receipt.marketingStatus) {
        if (receipt.marketingStatus === 'sending') { receipt.marketingStatus = 'unknown'; persistContact(); }
        onMarketingStatus(receipt.marketingStatus);
        return;
      }
      if (contact.marketingConsentVersion !== 'PARATITO_B2B_EMAIL_V1_2026-10-04' || typeof contact.marketingConsentText !== 'string' || !contact.marketingConsentText.trim() || contact.marketingConsentText.length > 1200) return;
      receipt.marketingStatus = 'sending';
      persistContact();
      try {
        const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=WewGaJ', {
          method: 'POST', credentials: 'omit', headers: { Accept: 'application/vnd.api+json', 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' }, signal: AbortSignal.timeout(12000),
          body: JSON.stringify({ data: { type: 'subscription', attributes: { custom_source: 'PARATITO Firmenkunden Angebotsanfrage', profile: { data: { type: 'profile', attributes: { email: contact.email, organization: contact.company, properties: { B2BContactName: contact.name, B2BEmailConsentText: contact.marketingConsentText, B2BEmailConsentVersion: contact.marketingConsentVersion }, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } }, relationships: { list: { data: { type: 'list', id: 'RKpQ2s' } } } } })
        });
        receipt.marketingStatus = response.status === 202 ? 'accepted' : 'failed';
      } catch { receipt.marketingStatus = 'unknown'; }
      persistContact();
      onMarketingStatus(receipt.marketingStatus);
    };
    const task = run();
    contactRequests.set(receipt.requestId, task);
    try { await task; } finally { contactRequests.delete(receipt.requestId); }
  };
  const initialize = scope => scope.querySelectorAll('[data-pt-separate]').forEach(root => {
    if (root.dataset.flowInitialized === '1') return;
    root.dataset.flowInitialized = '1';
    const copy = JSON.parse(root.querySelector('[data-pt-copy]').textContent);
    let catalog = JSON.parse(root.querySelector('[data-pt-catalog]').textContent);
    const form = root.querySelector('#FirmenkundenForm');
    const generatorLink = root.querySelector('[data-pt-generator-link]');
    const preparationStatus = root.querySelector('[data-pt-preparation-status]');
    const retry = root.querySelector('[data-pt-prepare-retry]');
    const extraFields = root.querySelector('[data-b2b-contact-fields]');
    const storageKey = 'paratito-inquiry-separate-v1';
    let reference = null, revision = 0, timer = null, preparedFingerprint = null, queuedSend = false;
    const sendButton = root.querySelector('[data-pt-send]');
    const selection = root.querySelector('[data-pt-selection]');
    const stock = root.querySelector('[data-pt-stock]');
    const transferError = root.querySelector('[data-pt-transfer-error]');
    const sendError = root.querySelector('[data-pt-send-error]');
    const restoreStatus = root.querySelector('[data-pt-restore-status]');
    const restoreActions = root.querySelector('[data-pt-restore-actions]');
    const choiceFieldset = root.querySelector('[data-pt-choice-fieldset]');
    const errors = root.querySelector('[data-b2b-contact-errors]');
    let restoring = false, restoreSequence = 0, restoreController = null;
    const controller = new AbortController();
    const { signal } = controller;
    let product = null;
    let variant = null;
    let captured = null, cachedDesignKey = null;
    let engraving = true;
    let step = 1;
    let busy = false;
    let pending = null;
    let prepared = null;
    let previewUrls = new Map();
    let reviewView = 'front';
    const zoomDialog = root.querySelector('[data-pt-preview-dialog]');
    const designerDialog = root.querySelector('[data-pt-designer-dialog]');
    const designerFrame = root.querySelector('[data-pt-designer-frame]');
    let designerNonce = null, designerReturnReference = null, designerOpener = null;
    let designerPreviousOverflow = '';
    const releasePreviews = () => { previewUrls.forEach(url => URL.revokeObjectURL(url)); previewUrls.clear(); };
    const number = value => new Intl.NumberFormat(document.documentElement.lang || 'de', { maximumFractionDigits: 1 }).format(value);
    const capacity = value => value === 650 ? '650 ml' : `${number(value / 1000)} L`;
    const label = () => `${copy[product.id]} · ${variant.color === 'Schwarz' ? copy.black : copy.blue} · ${capacity(variant.sizeMl)}`;
    const matches = () => product?.id === 'bottle' && variant?.color === 'Schwarz' && variant?.sizeMl === 1000;
    const clearPrepared = () => {
      revision++; pending = null; prepared = null; preparedFingerprint = null;
      extraFields.replaceChildren(); queuedSend = false; sendButton.disabled = restoring; sendButton.textContent = copy.send;
      root.querySelector('[data-pt-downloads]').hidden = true;
      preparationStatus.textContent = copy.complete_fields;
    };
    const snapshot = () => [...root.querySelectorAll('[data-pt-field]')].map(field => [field.dataset.ptField, field.type === 'checkbox' ? field.checked : field.value]);
    const fingerprint = () => JSON.stringify([variant?.sku, engraving, reference, snapshot()]);
    const persist = () => { try { sessionStorage.setItem(storageKey, JSON.stringify({ productId: product?.id, sku: variant?.sku, reference, cachedDesign:cachedDesignKey, engraving, step, fields: snapshot(), sending: false })); } catch {} };
    const updateLink = () => {
      if (!variant) return;
      const url = new URL(origin + '/');
      const returnUrl = new URL(location.pathname, location.origin);
      const previewTheme = new URLSearchParams(location.search).get('preview_theme_id') || (window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main' ? String(window.Shopify.theme.id || '') : '');
      if (/^\d+$/.test(previewTheme)) returnUrl.searchParams.set('preview_theme_id', previewTheme);
      Object.entries({variant: variant.shopVariantId, model: copy[product.id], color: variant.color, size: `${variant.sizeMl} ml`, occasion: root.querySelector('[data-pt-field="occasion"]').value.trim(), quantity: root.querySelector('[data-pt-field="quantity"]').value.trim(), returnUrl: returnUrl.href, ...(previewTheme && /^\d+$/.test(previewTheme) ? {preview_theme_id: previewTheme} : {})}).forEach(([key,value]) => url.searchParams.set(key,value));
      root.querySelectorAll('[data-pt-generator-link],[data-pt-generator-reopen]').forEach(link => { link.href = url.href; });
    };
    const updateNav = () => root.querySelectorAll('[data-pt-nav]').forEach(button => {
      const target = Number(button.dataset.ptNav);
      button.disabled = restoring || (target > 1 && !variant) || (target === 3 && engraving && !captured);
      if (target === step) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    const setRestoring = active => {
      restoring = active;
      root.dataset.ptRestoring = String(active);
      choiceFieldset.disabled = active;
      root.querySelectorAll('[data-pt-back],[data-pt-no-engraving],[data-pt-errors-edit]').forEach(button => { button.disabled = active; });
      root.querySelectorAll('[data-pt-start],[data-pt-generator-link],[data-pt-generator-reopen]').forEach(link => {
        if (active) { link.setAttribute('aria-disabled','true'); link.setAttribute('tabindex','-1'); }
        else { link.removeAttribute('aria-disabled'); link.removeAttribute('tabindex'); }
      });
      restoreStatus.setAttribute('aria-busy', String(active));
      sendButton.disabled = active || queuedSend;
      updateNav();
    };
    const showError = message => { sendError.textContent = message; sendError.hidden = false; sendError.focus({ preventScroll: true }); sendError.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); };
    const showStep = target => {
      if (restoring) return;
      if ((target > 1 && !variant) || (target === 3 && engraving && !captured)) return;
      if (target === 2) { openDesigner(); return; }
      step = target;
      root.querySelectorAll('[data-pt-step]').forEach(panel => { panel.hidden = Number(panel.dataset.ptStep) !== target; });
      updateNav();
      const heading = root.querySelector(`[data-pt-step="${target}"] h2`);
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({ block: 'start', behavior: 'smooth' });
      if (target === 3) { renderReview(); schedulePreparation(); }
      persist();
    };
    const logoMeasure = () => {
      if (!captured) return null;
      const measure = captured.settings.engravingDimensions;
      const ratio = measure.mode === 'estimated' ? dimensions(variant.sizeMl)[0] / 91 : 1;
      return { widthMm: measure.widthMm * ratio, heightMm: measure.heightMm * ratio, mode: measure.mode };
    };
    const productPicture = () => {
      const colorTerm = variant.color === 'Schwarz' ? /schwarz|black/i : /blau|tuerkis|turkis|blue/i;
      return product.images.find(item => colorTerm.test(item.url)) || product.images[0];
    };
    const requestSummary = () => `${valueOf('occasion')} · ${copy.approximate} ${valueOf('quantity')} · ${label()}`;
    const showReviewView = view => {
      if (!previewUrls.has(view)) return;
      reviewView = view;
      const reviewImage = root.querySelector('[data-pt-review-image]');
      reviewImage.src = previewUrls.get(view); reviewImage.alt = copy['view_' + view];
      root.querySelector('[data-pt-view-label]').textContent = copy['view_' + view];
      root.querySelectorAll('[data-pt-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ptView === view)));
      root.querySelector('[data-pt-zoom-image]').src = reviewImage.src;
      root.querySelector('[data-pt-zoom-image]').alt = reviewImage.alt;
      root.querySelector('[data-pt-zoom-label]').textContent = reviewImage.alt;
    };
    const renderReview = () => {
      const review = root.querySelector('[data-pt-review]');
      const reviewImage = root.querySelector('[data-pt-review-image]');
      if (zoomDialog.open) zoomDialog.close();
      releasePreviews();
      if (engraving && captured) views.forEach(view => previewUrls.set(view, URL.createObjectURL(captured.previews[view])));
      const hasPreview = previewUrls.size === 6;
      root.querySelector('[data-pt-engraving-figure]').hidden = !hasPreview;
      root.querySelector('[data-pt-gallery]').hidden = !hasPreview;
      root.querySelector('[data-pt-transfer-confirmation]').hidden = !hasPreview;
      root.querySelector('[data-pt-transferred-variant]').textContent = hasPreview ? label() : '';
      const thumbnails = root.querySelector('[data-pt-review-thumbnails]');
      thumbnails.replaceChildren();
      if (hasPreview) {
        views.forEach(view => {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'pt-view'; button.dataset.ptView = view;
          const photo = document.createElement('img'); photo.src = previewUrls.get(view); photo.alt = ''; photo.width = 120; photo.height = 100;
          const name = document.createElement('span'); name.textContent = copy['view_' + view];
          button.append(photo, name); button.addEventListener('click', () => showReviewView(view), { signal }); thumbnails.append(button);
        });
        showReviewView(captured.settings.selectedView);
      } else { reviewImage.removeAttribute('src'); root.querySelector('[data-pt-zoom-image]').removeAttribute('src'); }
      const picture = productPicture(), requestedImage = root.querySelector('[data-pt-requested-image]');
      requestedImage.hidden = !picture;
      if (picture) { requestedImage.src = picture.url; requestedImage.alt = label(); }
      root.querySelector('[data-pt-requested-label]').textContent = label();
      root.querySelector('[data-pt-logo-name]').textContent = engraving ? captured.originalFile.name : copy.no_engraving;
      const measure = engraving ? logoMeasure() : null;
      root.querySelector('[data-pt-logo-measure]').textContent = measure ? `${copy.logo_dimensions}: ${number(measure.widthMm)} × ${number(measure.heightMm)} mm (${copy[measure.mode]})` : '';
      root.querySelector('[data-pt-logo-settings]').textContent = engraving ? `${copy.orientation_label}: ${copy['orientation_' + captured.settings.orientation]} · ${copy.preview_size}: ${number(captured.settings.size)} %` : '';
      const [diameter, height] = dimensions(variant.sizeMl);
      root.querySelector('[data-pt-bottle-measure]').textContent = `${copy.bottle_dimensions}: Ø ${number(diameter)} × ${number(height)} mm`;
      root.querySelector('[data-pt-files-note]').textContent = engraving ? copy.files_note : copy.no_engraving_files;
      review.dataset.engraving = String(engraving);
      root.querySelector('[data-pt-request-summary]').textContent = requestSummary();
    };
    const selectVariant = selected => {
      if (variant?.sku !== selected.sku) { deleteDraft(cachedDesignKey); cachedDesignKey=null; captured = null; reference = null; releasePreviews(); root.querySelector('[data-pt-transfer-confirmation]').hidden=true; if(zoomDialog.open) zoomDialog.close(); }
      variant = selected;
      clearPrepared();
      selection.hidden = false;
      root.querySelector('[data-pt-selection-label]').textContent = label();
      const picture = productPicture();
      if (picture) {
        root.querySelector('[data-pt-selection-image]').src = picture.url;
        const externalImage = root.querySelector('[data-pt-external-image]');
        if(externalImage) externalImage.src = picture.url;
        const selectedImage = root.querySelector(`[data-pt-product="${product.id}"] img`);
        if (selectedImage) { selectedImage.removeAttribute('srcset'); selectedImage.src = picture.url; selectedImage.alt = label(); }
      }
      stock.textContent = variant.available ? copy.available : copy.unavailable;
      const referenceNote = root.querySelector('[data-pt-reference]');
      if(referenceNote) referenceNote.hidden = matches();
      renderOptions();
      updateLink();
      updateNav();
      persist();
    };
    const renderOptions = () => {
      const colors = root.querySelector('[data-pt-colors]');
      const sizes = root.querySelector('[data-pt-sizes]');
      colors.replaceChildren(); sizes.replaceChildren();
      const option = (text, selected, available, click) => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'pt-option'; button.dataset.ptOption = '1'; button.dataset.ptAvailable = String(available); button.setAttribute('aria-pressed', String(selected));
        const name = document.createElement('span'); name.textContent = text;
        const status = document.createElement('small'); status.dataset.ptOptionStock = '1'; status.textContent = available ? copy.option_available : copy.option_unavailable;
        const details = document.createElement('span'); details.className = 'pt-option-details'; details.append(name, status); button.append(details);
        button.addEventListener('click', click, { signal });
        return button;
      };
      [...new Set(product.variants.map(value => value.color))].sort().forEach(color => {
        const target = product.variants.find(value => value.color === color && value.sizeMl === variant.sizeMl) || product.variants.find(value => value.color === color && value.available) || product.variants.find(value => value.color === color);
        const button = option(color === 'Schwarz' ? copy.black : copy.blue, color === variant.color, target.available, () => selectVariant(target));
        const swatch = document.createElement('span'); swatch.className = 'pt-swatch'; swatch.dataset.color = color; swatch.setAttribute('aria-hidden', 'true'); button.prepend(swatch); colors.append(button);
      });
      product.variants.filter(value => value.color === variant.color).sort((a, b) => a.sizeMl - b.sizeMl).forEach(value => sizes.append(option(capacity(value.sizeMl), value.sku === variant.sku, value.available, () => selectVariant(value))));
    };
    const selectProduct = id => {
      if (restoring) return;
      const item = catalog.products.find(value => value.id === id);
      if (!item || !item.variants.length) return;
      product = item;
      root.querySelectorAll('[data-pt-product]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ptProduct === id)));
      root.querySelector('[data-pt-variant]').hidden = false;
      selectVariant(item.variants.find(value => value.color === 'Schwarz' && value.sizeMl === 1000 && value.available) || item.variants.find(value => value.available) || item.variants[0]);
      root.querySelector('[data-pt-variant]').scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    const makeImages = async configuration => {
      const source = engraving ? captured.previews : null;
      const manifest = source ? null : await getJson('/gallery/reference/manifest.json');
      if (!source && (!Array.isArray(manifest.templates) || manifest.templates.length !== 6)) throw new Error('images');
      return Promise.all(views.map(async view => {
        let blob = source?.[view];
        if (!blob) {
          const item = manifest.templates.find(value => value.id === view);
          if (!item || !/^\/gallery\/reference\/[a-z]+-[a-f0-9]{64}\.png$/.test(item.url)) throw new Error('images');
          const response = await fetch(origin + item.url, { credentials: 'omit' });
          if (!response.ok) throw new Error('images');
          blob = await response.blob();
        }
        const photo = await image(blob);
        const canvas = document.createElement('canvas'); canvas.width = 1200; canvas.height = 1000;
        const context = canvas.getContext('2d'); context.drawImage(photo, 0, 0, 1200, 1000);
        if (!configuration.previewMatchesVariant) {
          context.fillStyle = '#fff7e9'; context.fillRect(0, 0, 1200, 75);
          context.fillStyle = '#51340e'; context.font = 'bold 19px Arial'; context.fillText(copy.sample_banner, 20, 28, 1160);
          context.font = '18px Arial'; context.fillText(`${copy.requested_banner} ${label()}`, 20, 55, 1160);
        }
        return [`preview_${view}`, await canvasBlob(canvas, 'image/jpeg'), `preview_${view}.jpg`];
      }));
    };
    const valueOf = key => root.querySelector(`[data-pt-field="${key}"]`).value.trim();
    const choiceValid = () => { const field = root.querySelector('[data-pt-field="quantity"]'); if (!field.checkValidity()) { if(step !== 1) showStep(1); field.reportValidity(); return false; } return !!variant; };
    const makePacket = async () => {
      const value = key => root.querySelector(`[data-pt-field="${key}"]`).value.trim();
      const quantity = Number(value('quantity'));
      const fields = { name: value('name'), company: value('company'), email: value('email'), phone: value('phone'), notes: [copy.quantity_note, value('notes')].filter(Boolean).join('\n\n'), occasion: value('occasion'), quantityRange: quantity <= 24 ? 'Bis 24' : quantity <= 49 ? '25-49' : quantity <= 99 ? '50-99' : quantity <= 249 ? '100-249' : '250+', quantityExact: quantity, productId: product.id, sku: variant.sku, engraving, privacyAccepted: root.querySelector('[data-pt-field="privacy"]').checked, marketingOptIn: root.querySelector('[data-pt-field="marketing"]').checked, marketingConsentVersion: 'PARATITO_B2B_EMAIL_V1_2026-10-04', marketingConsentText: copy.marketing_consent, requestSource: 'shopify_firmenanfrage' };
      const [diameter, height] = dimensions(variant.sizeMl);
      const notice = matches() ? '' : `${copy.sample_banner}. ${copy.requested_banner} ${label()}. Farbe, Proportionen und Verschluss sind beispielhaft dargestellt.`;
      const settings = captured?.settings;
      const configuration = { orientation: engraving ? settings.orientation : 'horizontal', size: engraving ? settings.size : 60, removeBackground: engraving ? settings.removeBackground : false, dimensions: engraving ? logoMeasure() : null, isPlaceholder: false, logoName: engraving ? captured.originalFile.name : '', sourceWidth: null, sourceHeight: null, bottleDiameterMm: diameter, bottleHeightMm: height, previewMatchesVariant: matches(), previewNotice: notice, selectedView: engraving ? settings.selectedView : 'front' };
      if (engraving && settings.engravingDimensions.mode === 'requested') configuration.requestedWidthMm = settings.engravingDimensions.widthMm;
      const files = await makeImages(configuration);
      if (engraving) {
        const original = captured.originalFile;
        const logo = await image(original.bytes);
        const artwork = logoArtwork(logo, settings.removeBackground, settings.orientation);
        configuration.sourceWidth = artwork.width; configuration.sourceHeight = artwork.height;
        files.push(['artwork', await canvasBlob(artwork.canvas, 'image/png'), 'artwork.png']);
        files.push(['original', original.bytes, original.name]);
      }
      const original = files.find(([name]) => name === 'original');
      const originalHash = original ? [...new Uint8Array(await crypto.subtle.digest('SHA-256', await original[1].arrayBuffer()))].map(value => value.toString(16).padStart(2, '0')).join('') : null;
      return { requestId: crypto.randomUUID(), form: fields, configuration, files, variant: { ...variant }, originalHash };
    };
    const accepted = (value, packet) => {
      if (!value || value.status !== 'prepared' || value.requestId !== packet.requestId || !/^PT-\d{8}-[0-9A-F]{8}$/.test(value.inquiryId || '')) return false;
      if (JSON.stringify(value.form) !== JSON.stringify(packet.form)) {
        if (Object.keys(packet.form).some(key => value.form?.[key] !== packet.form[key])) return false;
      }
      if (!value.variant || ['productId', 'sku', 'color', 'sizeMl', 'shopVariantId'].some(key => value.variant[key] !== packet.variant[key])) return false;
      if (Object.keys(packet.configuration).filter(key => key !== 'previewNotice').some(key => JSON.stringify(value.configuration?.[key]) !== JSON.stringify(packet.configuration[key]))) return false;
      if (typeof value.configuration.previewNotice !== 'string' || value.configuration.previewNotice.length > 1200 || (!packet.configuration.previewMatchesVariant && !value.configuration.previewNotice.trim())) return false;
      if (!Number.isFinite(Date.parse(value.preparedAt)) || value.retention?.expiresAt !== null || !value.product || value.product.id !== packet.form.productId) return false;
      const pdf = value.downloads?.pdf;
      if (!pdf || pdf.contentType !== 'application/pdf' || !Number.isSafeInteger(pdf.bytes) || pdf.bytes < 1 || pdf.bytes > 32 * 1024 * 1024 || !/^[0-9a-f]{64}$/.test(pdf.sha256 || '')) return false;
      const original = packet.files.find(([name]) => name === 'original');
      if (original ? value.downloads?.original?.sha256 !== packet.originalHash || value.downloads.original.bytes !== original[1].size : value.downloads?.original !== null) return false;
      return !!value.downloads?.pdf?.url && (!engraving || !!value.downloads?.original?.url);
    };
    const normalize = inquiry => {
      const f = inquiry.form, c = inquiry.configuration, v = inquiry.variant;
      const download = (file, kind) => {
        const url = new URL(file.url);
        if (![origin, 'https://paratito.com', 'https://www.paratito.com'].includes(url.origin) || url.username || url.password || url.hash || url.pathname !== `/api/business/downloads/${inquiry.requestId}/${kind}` || !/^[a-f0-9]{64}$/.test(url.searchParams.get('key') || '') || [...url.searchParams].length !== 1) throw new Error('download');
        return origin + url.pathname + url.search;
      };
      return { requestId: inquiry.requestId, inquiryId: inquiry.inquiryId, contact: { name: f.name, company: f.company, email: f.email, marketingOptIn: f.marketingOptIn === true, marketingConsentVersion: f.marketingConsentVersion, marketingConsentText: f.marketingConsentText }, pdfUrl: download(inquiry.downloads.pdf, 'pdf'), logoUrl: inquiry.downloads.original ? download(inquiry.downloads.original, 'original') : null, fields: {
        'contact[name]': f.name, 'contact[Firma]': f.company, 'contact[email]': f.email, 'contact[phone]': f.phone,
        'contact[Produkt]': copy[inquiry.product.id], 'contact[Farbe]': v.color, 'contact[Größe]': `${v.sizeMl} ml`, 'contact[Artikelnummer]': v.sku, 'contact[Varianten-ID]': v.shopVariantId || '',
        'contact[Stückzahl]': String(f.quantityExact), 'contact[Stückzahl-Angabe]': copy.quantity_note, 'contact[Anlass]': f.occasion, 'contact[Gravur]': f.engraving ? 'Ja' : 'Nein',
        'contact[Gravurbreite × Höhe]': c.dimensions ? `${number(c.dimensions.widthMm)} × ${number(c.dimensions.heightMm)} mm (${copy[c.dimensions.mode]})` : '',
        'contact[Flaschendurchmesser × Höhe]': `${c.bottleDiameterMm} × ${c.bottleHeightMm} mm`, 'contact[Vorschau-Hinweis]': c.previewNotice, 'contact[Datenschutz]': 'Ja', 'contact[Marketing-Einwilligung]': f.marketingOptIn ? 'Ja, Bestätigung per E-Mail erforderlich' : 'Nicht erteilt', 'contact[Einwilligungstext-Version]': f.marketingConsentVersion, 'contact[Einwilligungstext]': f.marketingOptIn ? f.marketingConsentText : '', 'contact[body]': f.notes || 'Firmenkunden-Anfrage'
      } };
    };
    const valid = () => !restoring && !!variant && (!engraving || !!captured) && form.checkValidity();
    const schedulePreparation = () => {
      clearTimeout(timer); persist();
      if (!valid()) { preparationStatus.textContent = copy.complete_fields; return; }
      timer = setTimeout(prepareInquiry, 850);
    };
    const prepareInquiry = async () => {
      if (!valid()) return;
      if (prepared && preparedFingerprint === fingerprint()) return;
      if (busy) { timer = setTimeout(prepareInquiry, 500); return; }
      const currentRevision = revision, currentFingerprint = fingerprint();
      busy = true; sendButton.disabled = queuedSend; retry.hidden = true;
      preparationStatus.textContent = copy.preparing; sendError.hidden = true;
      try {
        const fresh = await getJson('/api/business/catalog');
        const current = fresh.products?.find(value => value.id === product.id)?.variants.find(value => value.sku === variant.sku);
        if (!current || ['color','sizeMl','shopVariantId'].some(key => current[key] !== variant[key])) throw new Error('variant');
        const packet = pending || await makePacket();
        if (revision !== currentRevision) return;
        pending = packet;
        const session = await getJson('/api/business/session');
        if (!session.preparationReady || typeof session.token !== 'string') throw new Error('service');
        await new Promise(resolve => setTimeout(resolve, 3100));
        if (revision !== currentRevision) return;
        const body = new FormData();
        body.append('requestId', packet.requestId); body.append('form', JSON.stringify(packet.form)); body.append('configuration', JSON.stringify(packet.configuration)); body.append('token', session.token); body.append('website','');
        packet.files.forEach(([name,blob,filename]) => body.append(name,blob,filename));
        const response = await fetch(origin + '/api/business/inquiries', {method:'POST',credentials:'omit',body});
        const inquiry = await response.json();
        if (revision !== currentRevision || fingerprint() !== currentFingerprint) return;
        if (!response.ok || !accepted(inquiry,packet)) throw new Error('preparation');
        prepared = normalize(inquiry); preparedFingerprint = currentFingerprint;
        const fields = {...prepared.fields, 'contact[Anfrage-ID]': prepared.inquiryId, 'contact[Anfrage-PDF]': prepared.pdfUrl, 'contact[Original-Logo]': prepared.logoUrl || ''};
        extraFields.replaceChildren();
        Object.entries(fields).forEach(([name,value]) => {
          if ([...root.querySelectorAll('[data-pt-field]')].some(field => field.name === name)) return;
          const input = document.createElement('input'); input.type='hidden'; input.name=name; input.value=value; extraFields.append(input);
        });
        window.ParatitoB2BTracking?.applyToForm(form);
        root.querySelector('[data-pt-pdf]').href = prepared.pdfUrl;
        const originalLink = root.querySelector('[data-pt-original]'); originalLink.hidden = !prepared.logoUrl; if(prepared.logoUrl) originalLink.href = prepared.logoUrl;
        root.querySelector('[data-pt-downloads]').hidden = true;
        preparationStatus.textContent = engraving ? copy.ready_to_send : copy.ready_without_logo;
        sendButton.disabled = false;
        if (queuedSend) { queuedSend = false; form.requestSubmit(sendButton); }
      } catch {
        if (revision === currentRevision) { queuedSend=false; sendButton.disabled=false; sendButton.textContent=copy.send; preparationStatus.textContent = copy.prepare_error; retry.hidden=false; }
      } finally {
        busy=false;
        if (revision !== currentRevision) schedulePreparation();
      }
    };
    const readSaved = async (ref, requestSignal) => {
      if (!/^[a-f0-9]{48}$/.test(ref)) throw new Error('reference');
      const read = async file => {
        const response = await fetch(origin+'/api/saved-preview/read',{method:'POST',credentials:'omit',signal:requestSignal,headers:{'Content-Type':'application/json'},body:JSON.stringify({reference:ref,...(file?{file}:{})})});
        if(!response.ok) throw new Error('restore');
        return response;
      };
      const saved = await (await read()).json();
      const v = saved.metadata?.variant;
      const foundProduct = catalog.products.find(p => p.variants.some(item => item.shopVariantId === v?.variant));
      const foundVariant = foundProduct?.variants.find(item => item.shopVariantId === v.variant);
      if (!foundVariant || v.color !== foundVariant.color || v.size !== `${foundVariant.sizeMl} ml` || ![foundProduct.label,copy[foundProduct.id]].includes(v.model)) throw new Error('variant');
      if (!Array.isArray(saved.files) || saved.files.length !== 7) throw new Error('files');
      let total=0;
      const file = async field => {
        const entries = saved.files.filter(item=>item.field===field);
        if(entries.length!==1) throw new Error('files');
        const entry=entries[0];
        if(typeof entry.name!=='string' || entry.name.length>240 || !['image/png','image/jpeg','image/svg+xml'].includes(entry.type) || (field!=='original' && entry.type!=='image/png')) throw new Error('file');
        const response=await read(field);
        const raw=await response.blob();
        total+=raw.size;
        if(!raw.size || raw.size>8*1024*1024 || total>=32*1024*1024) throw new Error('size');
        return {entry,blob:new Blob([raw],{type:entry.type})};
      };
      const [original, ...photos] = await Promise.all([file('original'), ...views.map(view => file('preview_'+view))]);
      const previews=Object.fromEntries(views.map((view,index)=>[view,photos[index].blob]));
      const pkg={schemaVersion:1,isPlaceholder:false,createdAt:new Date().toISOString(),settings:saved.metadata.settings,originalFile:{name:original.entry.name,type:original.entry.type,size:original.blob.size,bytes:original.blob},previews};
      if(!validatePackage(pkg)) throw new Error('configuration');
      return {product:foundProduct,variant:foundVariant,captured:pkg};
    };
    const restoreSaved = async ref => {
      const sequence=++restoreSequence;
      restoreController?.abort();
      const request=new AbortController(); restoreController=request;
      setRestoring(true); clearPrepared();
      const currentRevision=revision;
      reference=ref; persist();
      transferError.textContent=copy.restore_loading; transferError.hidden=false;
      restoreStatus.hidden=false; restoreStatus.classList.remove('pt-error'); restoreActions.hidden=true;
      restoreStatus.focus({preventScroll:true}); restoreStatus.scrollIntoView({block:'start',behavior:'smooth'});
      try {
        await catalogReady;
        const saved=await readSaved(ref,request.signal);
        if(request.signal.aborted || signal.aborted || sequence!==restoreSequence) return;
        if(revision!==currentRevision) throw new Error('stale');
        setRestoring(false);
        product=saved.product; selectVariant(saved.variant);
        captured=saved.captured; engraving=true; reference=ref;
        root.querySelectorAll('[data-pt-product]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.ptProduct===product.id)));
        root.querySelector('[data-pt-variant]').hidden=false;
        transferError.hidden=true; restoreStatus.hidden=true; persist(); showStep(3);
      } catch {
        request.abort();
        if(signal.aborted || sequence!==restoreSequence) return;
        setRestoring(false);
        transferError.textContent=copy.restore_error; transferError.hidden=false;
        restoreStatus.hidden=false; restoreStatus.classList.add('pt-error'); restoreActions.hidden=false;
        showStep(1);
        restoreStatus.focus({preventScroll:true}); restoreStatus.scrollIntoView({block:'start',behavior:'smooth'});
      } finally {
        if(sequence===restoreSequence && !signal.aborted) setRestoring(false);
      }
    };
    root.querySelector('[data-pt-restore-retry]').addEventListener('click',()=>{if(!restoring && reference) restoreSaved(reference);},{signal});
    root.querySelector('[data-pt-restore-back]').addEventListener('click',()=>{
      restoreSequence++; restoreController?.abort(); setRestoring(false);
      reference=null; captured=null; engraving=true; restoreStatus.hidden=true; transferError.hidden=true;
      clearPrepared(); showStep(1);
    },{signal});
    const openDesigner = () => {
      if(restoring || !choiceValid() || designerDialog.open) return;
      updateLink(); clearPrepared();
      designerOpener = document.activeElement;
      designerReturnReference = null; designerNonce = null;
      root.querySelector('[data-pt-designer-selection]').textContent = label();
      root.querySelector('[data-pt-designer-loading]').hidden = false;
      designerPreviousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      step=2; updateNav(); persist();
      designerDialog.showModal();
      designerFrame.src = generatorLink.href;
    };
    designerDialog.addEventListener('close', () => {
      const returnReference = designerReturnReference;
      designerReturnReference = null; designerNonce = null;
      designerFrame.src = 'about:blank';
      document.documentElement.style.overflow = designerPreviousOverflow;
      if(returnReference) { restoreSaved(returnReference); return; }
      engraving = !!captured;
      if(!captured) reference=null;
      showStep(variant ? 3 : 1);
      if(designerOpener?.isConnected) designerOpener.focus({preventScroll:true});
    }, {signal});
    root.querySelector('[data-pt-designer-close]').addEventListener('click', () => designerDialog.close(), {signal});
    designerFrame.addEventListener('load', () => {
      if(!designerDialog.open) return;
      try {
        if(designerFrame.contentWindow.location.href==='about:blank') return;
      } catch {}
      root.querySelector('[data-pt-designer-loading]').hidden = true;
      try {
        const returned = new URL(designerFrame.contentWindow.location.href);
        if(returned.origin !== location.origin || returned.pathname !== location.pathname) return;
        const ref = new URLSearchParams(returned.hash.slice(1)).get('preview');
        if(ref==='clear') { deleteDraft(cachedDesignKey); cachedDesignKey=null; reference=null; captured=null; releasePreviews(); clearPrepared(); designerDialog.close(); }
        else if(/^[a-f0-9]{48}$/.test(ref || '')) { designerReturnReference=ref; designerDialog.close(); }
      } catch {}
    }, {signal});
    window.addEventListener('message', async event => {
      if(!designerDialog.open || event.origin!==origin || event.source!==designerFrame.contentWindow) return;
      const message=event.data;
      if(!message || message.protocol!==protocol || message.receiver!=='inquiry-form' || !uuid(message.nonce)) return;
      const respond=(type,accepted) => designerFrame.contentWindow.postMessage({protocol,receiver:'inquiry-form',nonce:message.nonce,requestId:message.requestId,type,accepted},origin);
      if(message.type==='paratito:hello') { designerNonce=message.nonce; respond('paratito:host-ready',true); }
      else if(message.type==='paratito:inquiry' && message.nonce===designerNonce && uuid(message.requestId)) {
        if(!variant || !validatePackage(message.payload)) { respond('paratito:rejected',false); return; }
        const selectedSku=variant.sku, key=crypto.randomUUID();
        let cachedKey=null;
        try { await draftStore('readwrite',store=>store.put({id:key,sku:selectedSku,expiresAt:Date.now()+86400000,captured:message.payload})); cachedKey=key; } catch {}
        if(!designerDialog.open || designerNonce!==message.nonce || variant?.sku!==selectedSku) { deleteDraft(cachedKey); return; }
        deleteDraft(cachedDesignKey); cachedDesignKey=cachedKey;
        captured=message.payload; engraving=true; reference=null;
        clearPrepared(); transferError.hidden=true; restoreStatus.hidden=true;
        respond('paratito:received',true);
        designerDialog.close();
      }
    }, {signal});
    root.querySelectorAll('[data-pt-generator-link],[data-pt-generator-reopen]').forEach(link => link.addEventListener('click',event => { event.preventDefault(); openDesigner(); },{signal}));
    retry.addEventListener('click',prepareInquiry,{signal});
    root.querySelectorAll('[data-pt-product]').forEach(button => button.addEventListener('click', () => selectProduct(button.dataset.ptProduct), { signal }));
    root.querySelectorAll('[data-pt-nav]').forEach(button => button.addEventListener('click', () => showStep(Number(button.dataset.ptNav)), { signal }));
    root.querySelectorAll('[data-pt-back]').forEach(button => button.addEventListener('click', () => showStep(Number(button.dataset.ptBack)), { signal }));
    root.querySelectorAll('[data-pt-start]').forEach(link => link.addEventListener('click', event => { event.preventDefault(); if(!restoring) showStep(1); }, { signal }));
    root.querySelector('[data-pt-no-engraving]').addEventListener('click', () => { if(!choiceValid()) return; deleteDraft(cachedDesignKey); cachedDesignKey=null; engraving = false; captured = null; reference = null; releasePreviews(); clearPrepared(); showStep(3); }, { signal });
    root.querySelectorAll('[data-pt-field]').forEach(field => field.addEventListener('input', () => { clearPrepared(); if (variant) { root.querySelector('[data-pt-request-summary]').textContent = requestSummary(); updateLink(); } schedulePreparation(); }, {signal}));
    root.querySelector('[data-pt-preview-zoom]').addEventListener('click', () => { if (previewUrls.has(reviewView)) zoomDialog.showModal(); }, { signal });
    root.querySelector('[data-pt-preview-close]').addEventListener('click', () => zoomDialog.close(), { signal });
    root.addEventListener('invalid', event => { if (event.target.dataset?.ptField === 'quantity' && step !== 1) showStep(1); }, {capture:true,signal});
    form.addEventListener('submit', event => {
      if(restoring) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      if (!form.checkValidity() || !variant || (engraving && !captured)) {
        event.preventDefault(); event.stopImmediatePropagation(); queuedSend=false; form.reportValidity(); return;
      }
      if(!prepared || preparedFingerprint !== fingerprint()) {
        event.preventDefault(); event.stopImmediatePropagation(); queuedSend=true; sendButton.textContent=copy.sending;
        schedulePreparation();
      } else {
        preparationStatus.textContent=copy.sending_status;
        const returnUrl = new URL(location.href);
        returnUrl.searchParams.set('contact_posted', 'true');
        const previewTheme = new URLSearchParams(location.search).get('preview_theme_id') || (window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main' ? String(window.Shopify.theme.id || '') : '');
        if (/^\d+$/.test(previewTheme)) returnUrl.searchParams.set('preview_theme_id', previewTheme);
        returnUrl.searchParams.set('pt_request', prepared.requestId);
        returnUrl.hash = 'pt-contact-success';
        const returnField = form.querySelector('input[name="return_to"]');
        if (returnField) returnField.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;
        persist();
        try {
          const saved=JSON.parse(sessionStorage.getItem(storageKey)); saved.sending=true;
          saved.receipt={requestId:prepared.requestId,inquiryId:prepared.inquiryId,contact:prepared.contact,pdfUrl:prepared.pdfUrl,logoUrl:prepared.logoUrl,summary:requestSummary(),variantLabel:label(),occasion:valueOf('occasion'),quantity:valueOf('quantity'),engraving,logoName:engraving ? captured.originalFile.name : copy.no_engraving,logoMeasure:root.querySelector('[data-pt-logo-measure]').textContent,bottleMeasure:root.querySelector('[data-pt-bottle-measure]').textContent,submittedAt:new Date().toISOString()};
          sessionStorage.setItem(storageKey,JSON.stringify(saved));
        } catch {}
      }
    }, {capture:true,signal});
    root.querySelector('[data-pt-errors-edit]')?.addEventListener('click',()=>showStep(variant && (!engraving || captured) ? 3 : 1),{signal});
    window.addEventListener('pageshow', event => {
      if(event.persisted && prepared) { sendButton.disabled=false; showError(copy.send_error); }
    }, {signal});
    const completed = root.querySelector('[data-b2b-contact-success]');
    const receiptDownload = (url, requestId, kind) => {
      if (typeof url !== 'string') throw new Error('receipt');
      const value = new URL(url);
      if (value.origin !== origin || value.username || value.password || value.hash || value.pathname !== `/api/business/downloads/${requestId}/${kind}` || !/^[a-f0-9]{64}$/.test(value.searchParams.get('key') || '') || [...value.searchParams].length !== 1) throw new Error('receipt');
      return value.href;
    };
    const showReceipt = () => {
      try {
        const stored=JSON.parse(sessionStorage.getItem(storageKey)), receipt=stored?.receipt;
        if (!matchesNativeReturn(receipt, location.search)) return;
        const timestamp=Date.parse(receipt?.submittedAt), age=Date.now()-timestamp;
        if(stored?.sending !== true || !receipt || !uuid(receipt.requestId) || !/^PT-\d{8}-[0-9A-F]{8}$/.test(receipt.inquiryId || '') || !Number.isFinite(age) || age < -60000 || age > 86400000 || typeof receipt.engraving !== 'boolean') throw new Error('receipt');
        for(const key of ['summary','variantLabel','occasion','quantity','logoName','logoMeasure','bottleMeasure']) if(typeof receipt[key] !== 'string' || receipt[key].length > 1000) throw new Error('receipt');
        if(!receipt.summary.trim() || !receipt.variantLabel.trim() || !/^\d{1,8}$/.test(receipt.quantity) || Number(receipt.quantity) < 1 || Number(receipt.quantity) > 10000000) throw new Error('receipt');
        const pdfUrl=receiptDownload(receipt.pdfUrl,receipt.requestId,'pdf');
        const logoUrl=receipt.engraving ? receiptDownload(receipt.logoUrl,receipt.requestId,'original') : null;
        root.querySelector('[data-pt-success-summary]').textContent=receipt.summary;
        root.querySelector('[data-pt-success-request-id]').textContent=receipt.inquiryId;
        root.querySelector('[data-pt-success-logo]').textContent=receipt.logoName;
        root.querySelector('[data-pt-success-measures]').textContent=[receipt.logoMeasure,receipt.bottleMeasure].filter(Boolean).join(' · ');
        root.querySelector('[data-pt-success-pdf]').href=pdfUrl;
        const original=root.querySelector('[data-pt-success-original]'); original.hidden=!logoUrl; if(logoUrl) original.href=logoUrl;
        root.querySelector('[data-pt-success-receipt]').hidden=false;
        root.querySelector('[data-pt-success-files-pending]').hidden=true;
        sessionStorage.setItem(storageKey,JSON.stringify({sending:true,receipt}));
        window.ParatitoB2BTracking?.confirmedInquiry(receipt.inquiryId,receipt.submittedAt);
        return receipt;
      } catch { try { sessionStorage.removeItem(storageKey); } catch {} }
    };
    if(completed) {
      root.querySelector('[data-b2b-generator-area]').hidden=true;
      root.querySelectorAll('[data-pt-start]').forEach(link=>{link.hidden=true;});
      completed.hidden = false;
      completed.focus({preventScroll:true}); completed.scrollIntoView({block:'start',behavior:'smooth'});
      const receipt = showReceipt();
      if (matchesNativeReturn(receipt, location.search)) {
        const marketingStatus = root.querySelector('[data-pt-marketing-status]');
        const marketingRetry = root.querySelector('[data-pt-marketing-retry]');
        const onMarketingStatus = status => {
          if (!receipt.contact?.marketingOptIn) return;
          marketingStatus.hidden = false;
          marketingStatus.textContent = status === 'accepted' ? copy.marketing_accepted : copy.marketing_error;
          marketingRetry.hidden = status === 'accepted' || status === 'sending';
        };
        const sync = retryMarketing => completeContact(receipt, { storageKey, onMarketingStatus, retryMarketing });
        void sync(false);
        marketingRetry.addEventListener('click', async () => {
          marketingRetry.disabled = true;
          await sync(true);
          marketingRetry.disabled = false;
        }, { signal });
      }
    }
    root.querySelector('[data-pt-new-inquiry]')?.addEventListener('click',()=>{try{sessionStorage.removeItem(storageKey);}catch{}},{signal});
    const restore = async () => {
      if(completed) return;
      let stored=null;
      try { stored=JSON.parse(sessionStorage.getItem(storageKey)); } catch {}
      if(stored?.receipt && !Array.isArray(stored.fields)) { try{sessionStorage.removeItem(storageKey);}catch{} stored=null; }
      const fields=new Map([...root.querySelectorAll('[data-pt-field]')].map(field=>[field.dataset.ptField,field]));
      if(Array.isArray(stored?.fields)) stored.fields.forEach(entry=>{ if(!Array.isArray(entry)) return; const [key,value]=entry; const field=fields.get(key); if(field) { if(field.type==='checkbox') field.checked=value===true; else if(typeof value==='string') field.value=value; } });
      const selected=catalog.products.find(p=>p.id===stored?.productId);
      if(selected) { selectProduct(selected.id); const item=selected.variants.find(v=>v.sku===stored.sku); if(item) selectVariant(item); }
      const hash=new URLSearchParams(location.hash.slice(1));
      if(hash.has('preview')) {
        await catalogReady;
        if(signal.aborted) return;
        const context=new URLSearchParams(location.search);
        const occasion=context.get('occasion'), quantity=context.get('quantity');
        const occasionField=fields.get('occasion'), quantityField=fields.get('quantity');
        if(occasionField && [...occasionField.options].some(option=>option.value===occasion)) occasionField.value=occasion;
        if(quantityField && /^\d{1,8}$/.test(quantity || '') && Number(quantity)>=1 && Number(quantity)<=10000000) quantityField.value=quantity;
        const returnedProduct=catalog.products.find(item=>item.variants.some(value=>String(value.shopVariantId)===context.get('variant')));
        const returnedVariant=returnedProduct?.variants.find(value=>String(value.shopVariantId)===context.get('variant'));
        if(returnedVariant) { selectProduct(returnedProduct.id); selectVariant(returnedVariant); }
      }
      const ref=hash.has('preview') ? hash.get('preview') : stored?.reference;
      if(ref==='clear') {
        restoreSequence++; restoreController?.abort(); setRestoring(false);
        deleteDraft(stored?.cachedDesign); cachedDesignKey=null;
        reference=null; captured=null; engraving=false; releasePreviews();
        if(zoomDialog.open) zoomDialog.close();
        transferError.hidden=true; restoreStatus.hidden=true; restoreActions.hidden=true;
        root.querySelector('[data-pt-transfer-confirmation]').hidden=true;
        clearPrepared();
        history.replaceState(null,'',location.pathname+location.search);
        showStep(variant ? 3 : 1); persist();
        return;
      }
      if(ref && /^[a-f0-9]{48}$/.test(ref)) { reference=ref; persist(); }
      if(hash.has('preview')) history.replaceState(null,'',location.pathname+location.search);
      if(ref) {
        await restoreSaved(ref);
      } else if(variant && stored?.engraving!==false && uuid(stored?.cachedDesign)) {
        try {
          const draft=await draftStore('readonly',store=>store.get(stored.cachedDesign));
          if(signal.aborted) return;
          if(draft?.sku===variant.sku && draft.expiresAt>Date.now() && validatePackage(draft.captured)) {
            captured=draft.captured; cachedDesignKey=draft.id; engraving=true; showStep(3);
          } else { deleteDraft(stored.cachedDesign); }
        } catch {}
      } else if(variant && stored?.engraving === false) { engraving=false; reference=null; showStep(stored.step===3 ? 3 : 1); }
      else if(errors) showStep(1);
    };
    const catalogReady=completed ? Promise.resolve() : getJson('/api/business/catalog').then(value => {
      if(!Array.isArray(value.products) || !value.products.length) return;
      catalog=value;
      if(product && variant) {
        const freshProduct=catalog.products.find(item=>item.id===product.id), freshVariant=freshProduct?.variants.find(item=>item.sku===variant.sku);
        if(freshVariant && ['color','sizeMl','shopVariantId'].every(key=>freshVariant[key]===variant[key])) { product=freshProduct; variant=freshVariant; stock.textContent=variant.available ? copy.available : copy.unavailable; renderOptions(); }
      }
    }).catch(()=>{root.querySelector('[data-pt-load-error]').hidden=false;});
    restore();
    document.addEventListener('shopify:section:unload', event => { if (!event.target.contains(root)) return; restoreSequence++; restoreController?.abort(); controller.abort(); clearTimeout(timer); if(zoomDialog.open) zoomDialog.close(); if(designerDialog.open) { designerDialog.close(); document.documentElement.style.overflow=designerPreviousOverflow; } releasePreviews(); }, { signal });
  });
  if(window.parent!==window && window.frameElement?.hasAttribute('data-pt-designer-frame')) return;
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();

